'use client';

import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLobbyBoards } from '@/hooks/use-lobby-boards';
import { useBoardsLand, type BoardLandResult } from '@/hooks/use-board-land';
import { lobbyPathForBoard } from '@/lib/lobby-routes';
import { formatCompactUsd } from '@/features/lobby/data/mock-prices';
import type { MockBoard } from '@/features/lobby/data/mock-boards';
import { AvatarWithFlair } from '@/components/prelaunch/avatar-with-flair';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn, truncateAddress } from '@/lib/utils';

/**
 * Section 2 — Most Valuable Territories. Figma node `78:729`.
 *
 * Live leaderboard of the top boards by Land Value, where land value is
 * the sum of the self-assessed price of every tax-paying (non-creator)
 * seat — NOT the asset/pool TVL. Boards are filtered to those with live
 * 7-day rewards, then ranked by land value.
 *
 * Each row is clickable (navigates to the board), and the final column
 * shows the board's land holders as a stack of identity avatars + a
 * "+N others" tail. Land value + holders both come from the per-board
 * grid endpoint via `useBoardsLand`, fetched once the table is near the
 * viewport (and shared with the hero's total).
 */
export function TerritoriesSection() {
  const router = useRouter();
  // Pull every live board (API caps at 250) so the leaderboard ranks
  // across all territories, not just the first page.
  const { pinned, curated, community, isLoading } = useLobbyBoards({ limit: 250 });

  // Candidate set: boards that paid out in the last 7 days. Land value
  // (and the final ranking) comes from the per-board fetch below.
  const activeBoards = useMemo(
    () =>
      [...pinned, ...curated, ...community].filter(
        (b) => parseUsdStr(b.rewards7d) > 0,
      ),
    [pinned, curated, community],
  );

  // Gate the (heavy) per-board land/holder fetches until the leaderboard
  // is near the viewport — the section is below the fold.
  const tableRef = useRef<HTMLDivElement>(null);
  const [enabled, setEnabled] = useState(false);
  useEffect(() => {
    const el = tableRef.current;
    if (!el || enabled) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setEnabled(true);
          io.disconnect();
        }
      },
      { rootMargin: '300px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [enabled]);

  const { byBoardId, isLoading: landLoading } = useBoardsLand(activeBoards, enabled);

  // Rank by land value (self-assessed sum), highest first; drop boards
  // with no tax-paying land once their value has resolved.
  const topTen = useMemo(() => {
    return [...activeBoards]
      .sort(
        (a, b) =>
          (byBoardId.get(b.id)?.landValueUsd ?? 0) -
          (byBoardId.get(a.id)?.landValueUsd ?? 0),
      )
      .slice(0, 10);
  }, [activeBoards, byBoardId]);

  const showSkeleton = (isLoading || (enabled && landLoading)) && byBoardId.size === 0;

  return (
    <section
      id="territories"
      className="relative isolate bg-black px-6 py-24 sm:px-8 lg:py-32 lg:px-12"
    >
      <div className="mx-auto max-w-7xl">
        <div className="mx-auto max-w-3xl text-center">
          <h2
            className="font-marketing text-4xl uppercase tracking-[0.01em] text-amber-50 sm:text-5xl lg:text-[48px] lg:leading-[1.04]"
            style={{
              textShadow:
                '0 0 60px rgba(255, 215, 120, 0.18), 0 1px 0 rgba(0, 0, 0, 0.6)',
            }}
          >
            Most valuable territories
          </h2>
          <p className="mt-4 font-mono text-[12px] uppercase tracking-[0.135em] text-amber-100 sm:text-[14px]">
            Live rankings of the boards the market values most.
          </p>
        </div>

        <div
          ref={tableRef}
          className="relative mt-12 overflow-hidden rounded-[2px] border border-amber-500/25 bg-gradient-to-b from-zinc-950/75 to-black/70 backdrop-blur-[6px] lg:mt-16"
        >
          <Table className="min-w-[720px]">
            <TableHeader>
              <TableRow className="border-amber-500/20 hover:bg-transparent">
                {COLUMNS.map((col) => (
                  <TableHead
                    key={col.key}
                    className={cn(
                      'h-[49px] font-mono text-[12px] uppercase tracking-[0.1em] text-zinc-500',
                      col.align === 'right' && 'text-right',
                    )}
                  >
                    {col.label}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>

            <TableBody>
              {showSkeleton
                ? Array.from({ length: 10 }).map((_, i) => (
                    <TerritoryRowSkeleton key={i} rank={i + 1} />
                  ))
                : topTen.map((board, i) => (
                    <TerritoryRow
                      key={board.id}
                      board={board}
                      rank={i + 1}
                      land={byBoardId.get(board.id)}
                      onOpen={() => router.push(lobbyPathForBoard(board.id))}
                    />
                  ))}
              {!isLoading && !showSkeleton && topTen.length === 0 && (
                <TableRow className="border-zinc-800 hover:bg-transparent">
                  <TableCell
                    colSpan={COLUMNS.length}
                    className="py-16 text-center font-mono text-[13px] uppercase tracking-[0.12em] text-zinc-500"
                  >
                    No boards yet — check back soon.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        <div className="mt-10 flex justify-center">
          <Link
            href="/lobby"
            className="inline-flex h-[42px] items-center justify-center rounded-[4px] border border-amber-400/45 bg-gradient-to-b from-amber-500/20 to-amber-500/5 px-6 font-mono text-[10px] uppercase tracking-[0.15em] text-amber-100 transition-colors hover:border-amber-400/70 hover:from-amber-500/30 hover:to-amber-500/10"
          >
            View all boards
          </Link>
        </div>
      </div>
    </section>
  );
}

const COLUMNS: Array<{ key: string; label: string; align?: 'right' }> = [
  { key: 'rank', label: 'Rank' },
  { key: 'board', label: 'Board' },
  { key: 'rewards', label: '7D Rewards' },
  { key: 'tvl', label: 'Land Value' },
  { key: 'holders', label: 'Land Holders' },
];

function TerritoryRow({
  board,
  rank,
  land,
  onOpen,
}: {
  board: MockBoard;
  rank: number;
  land: BoardLandResult | undefined;
  onOpen: () => void;
}) {
  const loading = !land || land.isLoading;
  return (
    <TableRow
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }}
      role="link"
      tabIndex={0}
      aria-label={`Open ${board.name}`}
      className="cursor-pointer border-zinc-800 transition-colors hover:bg-amber-500/5 focus-visible:bg-amber-500/5 focus-visible:outline-none"
    >
      <TableCell className="font-mono text-[16px] text-amber-500">
        #{rank}
      </TableCell>
      <TableCell className="font-marketing text-[16px] tracking-[-0.02em] text-amber-50">
        {board.name}
      </TableCell>
      <TableCell className="text-[14px] text-zinc-400">
        {board.rewards7d || '—'}
      </TableCell>
      <TableCell className="font-mono text-[16px] text-amber-100">
        {loading ? (
          <span className="inline-block h-4 w-16 animate-pulse rounded-sm bg-zinc-800 align-middle" />
        ) : (
          formatCompactUsd(land!.landValueUsd)
        )}
      </TableCell>
      <TableCell>
        <LandHoldersCell land={land} />
      </TableCell>
    </TableRow>
  );
}

/** How many holder avatars to show inline before collapsing to "+N". */
const HOLDER_AVATARS = 4;

function LandHoldersCell({ land }: { land: BoardLandResult | undefined }) {
  if (!land || land.isLoading) {
    return (
      <div className="flex items-center -space-x-1.5">
        {Array.from({ length: HOLDER_AVATARS }).map((_, i) => (
          <span
            key={i}
            className="h-6 w-6 animate-pulse rounded-[4px] bg-zinc-800 ring-2 ring-black"
          />
        ))}
      </div>
    );
  }

  const holders = land.holders;
  if (holders.length === 0) {
    return <span className="font-mono text-[13px] text-zinc-600">—</span>;
  }

  const shown = holders.slice(0, HOLDER_AVATARS);
  const others = Math.max(0, holders.length - HOLDER_AVATARS);

  return (
    <div className="flex items-center gap-1.5">
      <div className="flex items-center -space-x-1.5">
        {shown.map((h) => {
          const name = h.displayName || truncateAddress(h.address);
          return (
            <span
              key={h.address}
              title={`${name} · ${h.seatCount} ${h.seatCount === 1 ? 'tile' : 'tiles'}`}
              className="rounded-[4px] ring-2 ring-black"
            >
              <AvatarWithFlair
                avatarUrl={h.avatarUrl}
                address={h.address}
                level={h.level ?? 0}
                size="xs"
              />
            </span>
          );
        })}
      </div>
      {others > 0 && (
        <span className="font-mono text-[11px] uppercase tracking-[0.08em] text-zinc-500">
          +{others} others
        </span>
      )}
    </div>
  );
}

function TerritoryRowSkeleton({ rank }: { rank: number }) {
  return (
    <TableRow className="border-zinc-800 hover:bg-transparent">
      <TableCell className="font-mono text-[16px] text-amber-500/40">
        #{rank}
      </TableCell>
      <TableCell>
        <div className="h-4 w-24 animate-pulse rounded-sm bg-zinc-800" />
      </TableCell>
      <TableCell>
        <div className="h-4 w-16 animate-pulse rounded-sm bg-zinc-800" />
      </TableCell>
      <TableCell>
        <div className="h-4 w-20 animate-pulse rounded-sm bg-zinc-800" />
      </TableCell>
      <TableCell>
        <div className="flex items-center -space-x-1.5">
          {Array.from({ length: HOLDER_AVATARS }).map((_, i) => (
            <span key={i} className="h-6 w-6 animate-pulse rounded-[4px] bg-zinc-800 ring-2 ring-black" />
          ))}
        </div>
      </TableCell>
    </TableRow>
  );
}

/**
 * Parse a pre-formatted USD string (`"$612k"`, `"$1.2M"`, `"$38"`) back
 * to a number. Used only to test the 7-day-rewards "is this board
 * active" gate — mirrors `BoardDirectory`'s own re-parse of the
 * formatted display string. Returns `0` for empty / unparseable input.
 */
function parseUsdStr(s: string | undefined | null): number {
  if (!s) return 0;
  const match = s.replace(/^\$/, '').match(/([\d,.]+)\s*([kmb]?)/i);
  if (!match) return 0;
  const value = parseFloat(match[1].replace(/,/g, ''));
  const suffix = match[2]?.toLowerCase();
  const mult = suffix === 'b' ? 1e9 : suffix === 'm' ? 1e6 : suffix === 'k' ? 1e3 : 1;
  return Number.isFinite(value) ? value * mult : 0;
}
